import { useState, useEffect } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "./Reveal";

export default function CallbackForm({ selectedPlan, clearPlan }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (selectedPlan) {
      setForm((f) => ({ ...f, message: `Je suis intéressé(e) par la formule "${selectedPlan}".` }));
    }
  }, [selectedPlan]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Merci de renseigner votre nom, téléphone et email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, plan: selectedPlan || null });
      setDone(true);
      toast.success("Votre demande a bien été envoyée. Nous vous rappelons très vite.");
      setForm({ name: "", phone: "", email: "", message: "" });
      clearPlan?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-cream/70 border-0 border-b-2 border-forest/15 px-4 py-3.5 text-forest placeholder:text-forest/40 focus:outline-none focus:border-gold transition-colors rounded-t-lg";

  return (
    <section id="rappel" className="py-24 md:py-32 px-6 md:px-10 bg-cream" data-testid="callback-section">
      <div className="max-w-2xl mx-auto">
        <Reveal>
          <div className="text-center">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Être rappelé</span>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl font-normal tracking-tight text-forest leading-[1.1]">
              Demandez à être rappelé
            </h2>
            <p className="mt-5 text-forest/65">
              Laissez-nous vos coordonnées, un conseiller Proxilia vous rappelle pour un premier échange gratuit et sans engagement.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="glass rounded-[2rem] p-8 md:p-10 mt-10 shadow-[0_24px_60px_-30px_rgba(48,66,56,0.4)]">
            {done ? (
              <div className="flex flex-col items-center justify-center text-center py-12" data-testid="contact-success">
                <CheckCircle2 className="w-16 h-16 text-sage" strokeWidth={1.5} />
                <h3 className="mt-5 font-cormorant text-3xl text-forest">Merci !</h3>
                <p className="mt-3 text-forest/65 max-w-xs">Votre demande a bien été enregistrée. Notre équipe vous rappelle dans les plus brefs délais.</p>
                <button onClick={() => setDone(false)} className="mt-7 text-sm font-medium text-gold hover:underline" data-testid="contact-new-request">
                  Envoyer une autre demande
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                {selectedPlan && (
                  <div className="text-sm bg-gold/12 text-forest rounded-xl px-4 py-3" data-testid="selected-plan-banner">
                    Formule sélectionnée : <strong>{selectedPlan}</strong>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-forest/70 mb-1.5 block">Nom</label>
                  <input className={inputCls} placeholder="Votre nom" value={form.name} onChange={set("name")} data-testid="contact-name" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-medium text-forest/70 mb-1.5 block">Téléphone</label>
                    <input className={inputCls} placeholder="06 00 00 00 00" value={form.phone} onChange={set("phone")} data-testid="contact-phone" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-forest/70 mb-1.5 block">Email</label>
                    <input type="email" className={inputCls} placeholder="vous@email.fr" value={form.email} onChange={set("email")} data-testid="contact-email" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-forest/70 mb-1.5 block">Message</label>
                  <textarea rows={4} className={`${inputCls} resize-none`} placeholder="Parlez-nous de vos besoins…" value={form.message} onChange={set("message")} data-testid="contact-message" />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="group w-full bg-forest text-cream py-4 rounded-full font-medium transition-all hover:bg-forestdark active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {loading ? "Envoi…" : "Être rappelé"}
                  {!loading && <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
